import gql from 'graphql-tag'
import { splitError } from '@/helper/splitError'
import { getProvider } from '@/apollo'
import ActivePromotionsModule from '@/store/modules/promotion/activePromotions'
import PromoCodeModule from '@/store/modules/promotion/promoCode'

const initialPromotion = {
  bookablePromotion: null,
  inclusivePromotion: null
}

const STATUS_LOADED = 'LOADED'
// occurs for presale if not available in prefetch or in customer when request was unsuccessful
const STATUS_ERROR = 'ERROR'

const PromotionModule = {
  namespaced: true,
  modules: {
    activePromotions: ActivePromotionsModule,
    promoCode: PromoCodeModule
  },
  state: {
    loadingStatus: {
      customerPromotion: '',
      presalesInclusive: '',
      presalesBookable: ''
    },
    promotions: initialPromotion,
    presalesPromotions: initialPromotion,
    preactivePromotions: initialPromotion,
    positionConfig: [],
    filterConfig: []
  },
  getters: {
    // generics
    promotions: state => {
      return state.promotions
    },
    bookablePromotion: state => {
      return (state.promotions?.bookablePromotion || []).map(promo => {
        promo.limited = false
        if (promo.duration && promo.duration.amount && promo.duration.amount < 24 && promo.duration.amount > 0) {
          promo.limited = true
        }
        return promo
      }) || []
    },
    inclusivePromotion: state => {
      return (state.promotions?.inclusivePromotion || []).map(promo => {
        promo.limited = false
        if (promo.duration && promo.duration.amount && promo.duration.amount < 24 && promo.duration.amount > 0) {
          promo.limited = true
        }
        return promo
      }) || []
    },
    promotionLoadingStatus: state => {
      return state.loadingStatus.customerPromotion
    },
    presalesBookablePromotion: state => {
      return (state.presalesPromotions?.bookablePromotion || []).map(promo => {
        promo.limited = false
        if (promo.duration && promo.duration.amount && promo.duration.amount < 24 && promo.duration.amount > 0) {
          promo.limited = true
        }
        return promo
      }) || []
    },
    presalesInclusivePromotion: state => {
      return (state.presalesPromotions?.inclusivePromotion || []).filter(promo => {
        if (state.filterConfig && state.filterConfig.length > 0) {
          return state.filterConfig.includes(promo.promotionId)
        }
        return true
      }).map(promo => {
        promo.limited = false
        if (promo.duration && promo.duration.amount && promo.duration.amount < 24 && promo.duration.amount > 0) {
          promo.limited = true
        }
        return promo
      }) || []
    },

    preactiveInclusivePromotion: state => {
      return (state.preactivePromotions?.inclusivePromotion || []).filter(promo => {
        if (state.filterConfig && state.filterConfig.length > 0) {
          return state.filterConfig.includes(promo.promotionId)
        }
        return true
      }) || []
    },
    // filtered promotions
    presalesInclusivePromotionsForContext: (state, getters) => (context) => {
      return getters.presalesInclusivePromotion.filter(promo => promo.context === context)
    },
    presalesInclusivePromotionsForContextAndTariff: (state, getters) => (context, tariffId = null) => {
      return getters.presalesInclusivePromotionsForContext(context).filter(promo => {
        if (tariffId === null) return true
        const promoTariffIds = promo.criteria?.tariffIds || []
        // no tariffIds specified -> no criteria limitation -> applies to all
        if (promoTariffIds.length === 0) return true
        if (Array.isArray(tariffId)) {
          let foundAll = true
          tariffId.forEach(id => {
            if (promoTariffIds.length > 0 && promoTariffIds.includes(id) === false) foundAll = false
          })
          if (!foundAll) return false
        } else if (tariffId !== null) {
          if (promoTariffIds.length > 0 && promoTariffIds.includes(tariffId) === false) return false
        }
        return true
      })
    },
    presalesInclusivePromotionsForFilterSet: (state, getters) => ({
      context = null,
      frontendContext = null,
      tariffId = null,
      tariffOptionId = null,
      position = null,
      mnpProvider = null
    }) => {
      return getters.presalesInclusivePromotion.filter(promo => {
        if (context !== null && promo.context !== context) return false
        if (frontendContext !== null && promo.frontendContext !== frontendContext) return false
        if (position !== null) {
          // check if promotion element should be applied due to position config
          // return false if it should not be shown on this position
          // do NOT return true otherwise to keep later checks!
          if (getters.filteredPositionForPromotion(promo).find(pos => {
            return pos.name === position
          }) === undefined) {
            return false
          }
        }
        if (tariffId !== null && Array.isArray(tariffId)) {
          const tariffIds = promo.criteria?.tariffIds || []
          let foundAll = true
          tariffId.forEach(id => {
            if (tariffIds.length > 0 && tariffIds.includes(id) === false) foundAll = false
          })
          if (!foundAll) return false
        } else if (tariffId !== null) {
          const tariffIds = promo.criteria?.tariffIds || []
          if (tariffIds.length > 0 && tariffIds.includes(tariffId) === false) return false
        }

        const tariffoptionIds = promo.criteria?.tariffoptionIds || []
        if (tariffoptionIds.length > 0 && tariffoptionIds.includes(tariffOptionId) === false) return false

        const criteriaMnpProvider = promo.criteria?.mnpProvider || []
        if (criteriaMnpProvider.length > 0 && criteriaMnpProvider.includes(mnpProvider) === false) return false
        // if no false exit applies, keep promo
        return true
      })
    },
    normalizedPositions: (state) => {
      return state.positionConfig.map(({ page, position, component, promoIds, promotions }) => {
        return {
          name: `${component}_${page}_${position}`,
          promoIds,
          promotions
        }
      })
    },
    filteredPositionForPromotion: (state, getters) => promo => {
      return getters.normalizedPositions.filter(position => {
        if (!position.promotions) {
          // "negative values" (false, null, undefined) disable the position
          return false
        }
        if (position.promotions === true) {
          if (position.promoIds !== undefined && position.promoIds.length > 0 && !position.promoIds.includes(promo.promotionId)) {
            return false
          } else {
            // true enables the position always
            return true
          }
        }
        // empty array and all other values will disable
        if (Array.isArray(position.promotions) === false || position.promotions.length === 0) {
          return false
        }
        // otherwise, we expect an array with partial Promos that must match the promo.
        // check promotionId for the beginning
        return position.promotions.find(configPromo => {
          return configPromo.promotionId === promo.promotionId
        }) !== undefined
      })
    },
    getCartForSelection: (state, getters, rootState, rootGetters) => ({
      mode = 'checkout',
      tariffId = null,
      tariffoptionId = null,
      mnp = null,
      provider = null
    }) => {
      const tariff = rootState.checkout.tariff
      let targetTariff = null
      if (tariffId === null && mnp) {
        tariffId = '152'
      } else if (tariffId === null) {
        tariffId = '155'
      }
      window.$gqlpre.bookablePresalesTariffs.forEach(tariff => {
        if (tariff.tariffId === tariffId) {
          targetTariff = tariff
        }
      })
      let targetTariffoption = null
      if (tariffoptionId !== null) {
        tariff.defaultTariff.tariffOptions.bookablePresalesTariffOptions.forEach(option => {
          if (option.tariffoptionId === tariffoptionId) {
            targetTariffoption = [option]
          }
        })
      }

      const inclusivePromotions = mode === 'activation' ? getters.preactiveInclusivePromotion : getters.presalesInclusivePromotion

      const targetPromotions = inclusivePromotions.filter(promo => {
        // Tariff Filter
        const criteriaTariffIds = promo.criteria?.tariffIds || []
        if (criteriaTariffIds.length > 0 && criteriaTariffIds.includes(tariffId) === false) return false

        // Tariffoption Filter
        const criteriaTariffoptionIds = promo.criteria?.tariffoptionIds || []
        if (criteriaTariffoptionIds.length > 0 && criteriaTariffoptionIds.includes(tariffoptionId) === false) return false

        // Provider Filter
        const criteriaMnpProvider = promo.criteria?.mnpProvider || []
        if (criteriaMnpProvider.length > 0 && criteriaMnpProvider.includes(provider) === false) return false

        // Voucher Filter
        if (promo.requiredParameters !== null) return false
        // if no false exit applies, keep promo
        return true
      })
      return { targetTariff, targetTariffoption, targetPromotions }
    }
  },
  actions: {
    async getAvailablePresalesPromotions ({ dispatch, commit, state, getters, rootGetters }) {
      await dispatch('initializeAvailablePresalesPromotions')
    },
    async initializeAvailablePresalesPromotions ({ dispatch, commit, state, getters, rootGetters }) {
      // TODO: Quick Fix...
      state.loadingStatus.presalesBookable = ''
      state.loadingStatus.presalesInclusive = ''
      // split to be loadable individually
      await Promise.all([
        dispatch('initializePresale', { prefetchName: 'inclusiveAvailablePresalesPromotions', mutation: 'savePresaleInclusive' }),
        dispatch('initializePresale', { prefetchName: 'bookableAvailablePresalesPromotions', mutation: 'savePresaleBookable' })
      ])
    },
    async initializePresale ({ dispatch, commit, state, getters, rootGetters }, { prefetchName, mutation }) {
      commit('clearMessages', {}, { root: true })
      const prefetchData = window.$gqlpre?.[prefetchName] || undefined
      commit(mutation, { prefetchData })
    },
    async getAvailablePromotions ({ dispatch, commit, state, getters, rootGetters }) {
      await dispatch('initializeAvailablePromotionsForCustomer', { targetProcess: null })
    },
    async initializeAvailablePromotionsForCustomer ({ dispatch, commit, state, getters, rootGetters }, { targetProcess = null }) {
      // initialize
      commit('clearMessages', {}, { root: true })
      if (state.loadingStatus.customerPromotion !== STATUS_LOADED || targetProcess === 'belatedPorting') {
        commit('SET_PROMOTION', initialPromotion)
        commit('SET_PRESALES_PROMOTION', initialPromotion)

        const apolloClient = getProvider().defaultClient
        return apolloClient.query({
          query: gql`query availablePromotions ($targetProcess: String = null) {
            availablePromotions(promotionCode: null, targetProcess: $targetProcess) {
              bookablePromotion {
                ...fullPromo
              }
              inclusivePromotion {
                ...fullPromo
              }
              statusInfo
            }
          }
          fragment fullPromo on BookablePromotion {
            promotionId
            name
            description
            context
            bandwidth {
              up
              down
              unit
              throttled
              throttledUnit
              tech
              formattedUnit
              formattedThrottledUnit
            }
            volumes {
              formattedUnit
              type
              amount
              unit
            }
            incentives {
              id
              title
              description
              type
              ...on ServiceIncentive {
                service
              }
              ...on CreditIncentive {
                discount
                targetId
                targetEntity
                targetFee
                creditTarget: target
              }
              ...on OtherIncentive {
                kind
              }
              ...on VolumeIncentive {
                volumeTarget: target
                amount
                unit
              }
            }
            frontendContext
            notBookableWith
            requiredParameters
            criteria {
              tariffIds
              mnpProvider
            }
            duration {
              amount
              unit
              formattedUnit
            }
          }`,
          variables: {
            targetProcess: targetProcess
          }
        }).then((response) => {
          if (targetProcess === 'belatedPorting' && response.data.availablePromotions) {
            commit('savePresaleInclusive', { prefetchData: response.data.availablePromotions.inclusivePromotion })
            commit('savePresaleBookable', { prefetchData: response.data.availablePromotions.bookablePromotion })
          } else if (response.data.availablePromotions) {
            commit('SET_PROMOTION', response.data.availablePromotions)
            commit('storePromotionLoadingStatus', STATUS_LOADED)
          }
        }).catch((error) => {
          commit('storePromotionLoadingStatus', STATUS_ERROR)
          splitError(error).forEach(e => commit('addError', e, { root: true }))
          throw error
        })
      }
    },
    async loadAvailablePreactivePromotions ({ dispatch, commit, state, getters, rootGetters }, { msisdn }) {
      const apolloClient = getProvider().defaultClient
      return apolloClient.query({
        query: gql`query availablePreactivePromotions ($msisdn: String = null) {
          availablePreactivePromotions(brand: "simon", promotionCode: null, msisdn: $msisdn) {
            bookablePromotion {
              ...fullPromo
            }
            inclusivePromotion {
              ...fullPromo
            }
            statusInfo
          }
        }
        fragment fullPromo on BookablePromotion {
          promotionId
          name
          description
          context
          bandwidth {
            up
            down
            unit
            throttled
            throttledUnit
            tech
            formattedUnit
            formattedThrottledUnit
          }
          volumes {
            formattedUnit
            type
            amount
            unit
          }
          incentives {
            id
            title
            description
            type
            ...on ServiceIncentive {
              service
            }
            ...on CreditIncentive {
              discount
              targetId
              targetEntity
              targetFee
              creditTarget: target
            }
            ...on OtherIncentive {
              kind
            }
            ...on VolumeIncentive {
              volumeTarget: target
              amount
              unit
            }
          }
          frontendContext
          notBookableWith
          requiredParameters
          criteria {
            tariffIds
            mnpProvider
            tariffoptionIds
          }
          duration {
            amount
            unit
            formattedUnit
          }
        }`,
        variables: {
          msisdn: msisdn
        }
      }).then((response) => {
        if (response.data.availablePreactivePromotions) {
          commit('savePresaleInclusive', { prefetchData: response.data.availablePreactivePromotions.inclusivePromotion, force: true })
          commit('savePresaleBookable', { prefetchData: response.data.availablePreactivePromotions.bookablePromotion, force: true })
          commit('SET_PREACTIVE_PROMOTION', response.data.availablePreactivePromotions)
        }
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
        throw error
      })
    },
    initializePositionConfig ({ dispatch, commit, state, getters, rootGetters }, { positions }) {
      commit('storePositions', positions)
    },
    importConfigFromCto ({ dispatch, commit, state, getters, rootGetters }) {
      const config = window.$feconfig?.promotion || {}
      if (config.filter) {
        commit('storeFilter', config.filter)
      }
      if (config.positions) {
        commit('storePositions', config.positions)
      }
    }
  },
  mutations: {
    // customer
    SET_PROMOTION (state, promotions) {
      state.promotions = promotions
    },
    SET_PRESALES_PROMOTION (state, promotions) {
      state.presalesPromotions = promotions
    },
    SET_PREACTIVE_PROMOTION (state, promotions) {
      state.preactivePromotions = promotions
    },
    storePromotionLoadingStatus: (state, status) => {
      state.loadingStatus.customerPromotion = status
    },
    // presales
    savePresale: (state, { prefetchData, statusKey, dataKey }) => {
      // only load it once
      if (state.loadingStatus[statusKey] !== STATUS_LOADED) {
        if (typeof prefetchData === 'undefined') {
          state.loadingStatus[statusKey] = STATUS_ERROR
          return
        }
        // if null, it was loaded via prefetch but does not contain any data
        state.loadingStatus[statusKey] = STATUS_LOADED
        if (prefetchData !== null) {
          state.presalesPromotions[dataKey] = Object.values(prefetchData)
        }
      }
    },
    savePresaleInclusive: (state, { prefetchData, force = false }) => {
      if (force) {
        state.loadingStatus.presalesInclusive = ''
      }
      PromotionModule.mutations.savePresale(state, {
        prefetchData,
        statusKey: 'presalesInclusive',
        dataKey: 'inclusivePromotion'
      })
    },
    savePresaleBookable: (state, { prefetchData, force = false }) => {
      if (force) {
        state.loadingStatus.presalesBookable = ''
      }
      PromotionModule.mutations.savePresale(state, {
        prefetchData,
        statusKey: 'presalesBookable',
        dataKey: 'bookablePromotion'
      })
    },
    storePositions: (state, positions) => {
      state.positionConfig = positions
    },
    storeFilter: (state, filter) => {
      state.filterConfig = filter
    }
  }
}

export {
  PromotionModule
}
